import React, { Suspense, useMemo } from 'react';
import './App.css';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom';
import { NavigationListener } from 'components/NavigationListener';
import { NhostClient } from '@nhost/react';
import { AuthGate } from 'containers/Authgate';
import { PostInstall } from 'containers/PostInstall';
import { TemplatePreview } from 'containers/TemplatePreview';
import { AcceptInvite } from 'containers/AcceptInvite';
import { PostPayment } from 'containers/PostPayment';
import { KlaviyoMarketplaceInstall } from 'containers/KlaviyoMarketplaceInstall';
import { GorgiasMarketplaceInstall } from 'containers/GorgiasMarketplaceInstall';
import { Login } from './containers/Login';
import { ForgotPassword } from './containers/ForgotPassword';
import SignUp from './containers/Signup';
import { ThirdPartyChatLoaders } from 'components/ThirdPartyChatLoaders';
import { EmailChangeConfirmation } from 'containers/EmailChangeConfirmation';
import { prepareEmailChangeConfirmation } from 'utils/emailChangeConfirmation';

const NhostApolloProvider = React.lazy(() =>
  import('@nhost/react-apollo').then((module) => ({
    default: module.NhostApolloProvider,
  })),
);

const NhostReactProvider = React.lazy(() =>
  import('@nhost/react').then((module) => ({
    default: module.NhostProvider,
  })),
);

const emailChangeCallback = prepareEmailChangeConfirmation();
const nhost = new NhostClient({
  subdomain: process.env.REACT_APP_NHOST_SUBDOMAIN,
  region: process.env.REACT_APP_NHOST_REGION,
  autoSignIn: !emailChangeCallback,
  autoRefreshToken: !emailChangeCallback,
});
const confirmEmailChange = () =>
  emailChangeCallback
    ? emailChangeCallback(nhost.auth)
    : Promise.resolve({ status: 'invalid' as const });

const AppRouterLayout = () => (
  <>
    <ThirdPartyChatLoaders />
    <NavigationListener />
    <Outlet />
  </>
);

const App = () => {
  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          element: <AppRouterLayout />,
          children: [
            { path: 'invite/accept', element: <AcceptInvite /> },
            { path: 'post-install', element: <PostInstall /> },
            { path: 'post-payment', element: <PostPayment /> },
            {
              path: 'integrations/klaviyo/install',
              element: <KlaviyoMarketplaceInstall />,
            },
            {
              path: 'integrations/gorgias/install',
              element: <GorgiasMarketplaceInstall />,
            },
            { path: 'login', element: <Login /> },
            { path: 'forgot-password', element: <ForgotPassword /> },
            { path: 'sign-up', element: <SignUp /> },
            {
              path: 'email-change',
              element: <EmailChangeConfirmation confirm={confirmEmailChange} />,
            },
            { path: 'template-preview', element: <TemplatePreview /> },
            { path: '*', element: <AuthGate /> },
          ],
        },
      ]),
    [],
  );

  return (
    <Suspense fallback={<div></div>}>
      <NhostReactProvider nhost={nhost}>
        <NhostApolloProvider nhost={nhost}>
          <RouterProvider router={router} />
        </NhostApolloProvider>
      </NhostReactProvider>
    </Suspense>
  );
};

export default App;
